import { Box, Checkbox, FormControlLabel } from '@mui/material'
import Markdown from 'markdown-to-jsx'
import { useState } from 'react'
import {
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import FluentWarning20Filled from '~icons/fluent/warning-20-filled'
import { UIButton } from 'src/components/ui/Button'
import { UIDialog } from 'src/components/ui/Modal'
import { UITab, UITabList } from 'src/components/ui/Tab'
import Text from 'src/components/ui/Text'
import { publishModel } from 'src/services/helpers/model'
import {
  isUserLora as checkIsUserLora,
  hasModelTypeDisabledAddVersion,
  isLoraCategoryNotSet,
  isUserMultiLora,
} from 'src/utils/model'
import { track } from 'src/utils/tracker'
import { UITooltipHelpIcon } from '~/components/HelpIcon'
import ModelAssetForm from './ModelSubmitForms/ModelAssetForm'
import ModelInfoForm from './ModelSubmitForms/ModelInfoForm'
import { useModelSubmitForm } from './ModelSubmitForms/ModelSubmitFormProvider'
import ModelTrainingTasks from './ModelSubmitForms/ModelTrainingTaskForm'

export default function ModelEditContent(props: {
  onSuccess?: () => any
  onClose?: () => any
  mutate?: () => any
  appendCompForInfo?: JSX.Element
  highlightId?: string
}) {
  const [tabVal, setTabVal] = useState<'info' | 'version'>('info')

  const handleTabChange = (newValue: string | number) => {
    track('edit_model_tab_click', {
      tab: newValue,
    })
    setTabVal(newValue as 'info' | 'version')
  }

  const { t } = useTranslation(['action', 'market', 'artwork'])
  const { values, handleChange, submit } = useModelSubmitForm()

  const isEditing = !!values.modelId
  const isUserLora = checkIsUserLora(values.type)
  const showVersions = !hasModelTypeDisabledAddVersion(values.type)
  const showTrainingTasks = isUserMultiLora(values.type)

  const [toPublish, setToPublish] = useState(false)

  return (
    <div className="p-4 mmd:p-8 overflow-y-auto">
      <AriaTabs
        selectedKey={tabVal}
        onSelectionChange={handleTabChange}
        orientation="horizontal"
      >
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          className="flex items-center"
        >
          <UITabList variant="underlined">
            <UITab id="info" variant="underlined" className="px-4">
              {t('market:submit.info-tab.label')}
            </UITab>
            {showVersions && (
              <UITab id="version" variant="underlined" className="px-4">
                {t('market:submit.version-tab.label')}
              </UITab>
            )}
            {showTrainingTasks && (
              <UITab id="training-tasks" variant="underlined" className="px-4">
                {t('market:edit-model-form.training-tasks.tab.label')}
              </UITab>
            )}
          </UITabList>
          {tabVal === 'version' && (
            <div className="ml-auto">
              <UITooltipHelpIcon
                iconClassName="size-5"
                wrapperClassName="max-w-[400px] px-4 py-3"
              >
                <Markdown
                  options={{
                    forceBlock: true,
                    overrides: {
                      a: (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-underline text-violet-600 dark:text-violet-400"
                        />
                      ),
                    },
                  }}
                  className="prose prose-sm prose-neutral dark:prose-invert"
                >
                  {t('market:submit.version-tab.help.tooltip.content')}
                </Markdown>
              </UITooltipHelpIcon>
            </div>
          )}
        </Box>
        <AriaTabPanel id="info">
          <div className="overflow-y-auto max-h-[calc(100vh-64px-128px-32px-50px-44px)] p-4">
            <ModelInfoForm
              key={values.modelId}
              className="pt-2 h-full"
              isEditing={isEditing}
              values={values}
              handleChange={handleChange}
              appendComp={props.appendCompForInfo}
              highlightId={props.highlightId}
            />
          </div>
          {isLoraCategoryNotSet(values) && (
            <div className="mt-2 flex justify-end">
              <div className="flex items-start gap-2 px-3 py-2 text-sm bg-amber-100 dark:bg-yellow-500/20 rounded outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10">
                <FluentWarning20Filled className="size-5 text-amber-500" />
                <p>
                  <Text i18nKey="market:submit.messages.category-not-set" />
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center mt-2">
            <span className="m-auto" />
            {isUserLora && values.isPrivate && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={toPublish}
                    onChange={ev => setToPublish(ev.target.checked)}
                  />
                }
                label={t('artwork:generation.auto-publish')}
              />
            )}
            <UIButton
              busy="auto"
              disabled={isLoraCategoryNotSet(values)}
              onPress={async () => {
                if (toPublish) {
                  const toProceed = await UIDialog.confirm({
                    heading: t('market:model.auto-publish-lora.dialog.title'),
                    content: t('market:model.auto-publish-lora.dialog.content'),
                  })
                  if (!toProceed) return
                }

                try {
                  await submit()
                  if (toPublish && values.modelId) {
                    await publishModel(values.modelId)
                  }
                  toast.success(t('market:submit.notice.model-info-updated'))
                  props.mutate?.()
                  props.onClose?.()
                } catch (err: any) {
                  toast.error(err.message)
                }
              }}
              variant="filled"
            >
              <span>
                {toPublish ? t('action:save-and-publish') : t('action:save')}
              </span>
            </UIButton>
          </div>
          {toPublish && (
            <p className="mt-1 w-fit text-sm ml-auto text-neutral-500 dark:text-neutral-400">
              <Text
                i18nKey="market:submit.model-form.publish-agreement"
                defaults="By publishing, you allow all users to generate and share images using this model."
              />
            </p>
          )}
        </AriaTabPanel>
        {showVersions && (
          <AriaTabPanel
            id="version"
            className="overflow-y-auto max-h-[calc(100vh-64px-128px-32px-50px)] py-4"
          >
            <ModelAssetForm
              isEditing={isEditing}
              values={values}
              handleChange={handleChange}
              onClose={props.onClose}
            />
          </AriaTabPanel>
        )}
        {showTrainingTasks && values.modelId && (
          <AriaTabPanel
            id="training-tasks"
            className="overflow-y-auto max-h-[calc(100vh-64px-128px-32px-50px)] py-4"
          >
            <ModelTrainingTasks
              modelId={values.modelId}
              modelType={values.type}
              onClose={props.onClose}
            />
          </AriaTabPanel>
        )}
      </AriaTabs>
    </div>
  )
}
